import { Box, Button, CircularProgress } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import { httpClient } from "api/httpClient";
import { DownloadPalletsExportDropdown } from "components/dialogs/ExportDialog/DownloadPalletsExportDropdown";
import { envSettings } from "core/EnvSettings";
import { OrderStatus } from "entities/OrderStatus";
import React from "react";
import {
	TopToolbar,
	sanitizeListRestProps,
	useListContext,
	useNotify,
	usePermissions,
	useTranslate,
} from "react-admin";
import { downloadFileWithNotification } from "utils/download";
import { useNotifyError } from "utils/notifiers/useNotifyError";

import { UserRole } from "../../../../../entities/UserRole";
import {
	isScannerOperationMode,
	scanToCreateModeOption,
} from "../../PickingList/components/OperationModeToggleFilter";
import { useOrderServiceType } from "../hooks/useOrderServiceType";
import { usePalletGenerationGate } from "../hooks/usePalletGenerationGate";
import { DownloadPalletsLabelsDropdown } from "./DownloadPalletsLabelsDropdown";
import { GenerateBySourceDocPalletsButton } from "./GenerateBySourceDocPalletsButton";
import { PalletsFromPdfButton } from "./PalletsFromPdfButton";

export const PalletsActions = ({
	filters,
	order,
	operationMode,
	isLoadingMode,
	isMoveToPreparationMode,
	setIsLoadingMode,
	setIsMoveToPreparationMode,
	selectedPalletsForLoading,
	setSelectedPalletsForLoading,
	allocationLoading,
	setAllocationLoading,
	setAllocationResult,
	setAllocationResultOpen,
	statusChangeLoading,
	setStatusChangeLoading,
	setOperationMode,
	defaultOperationMode,
	allowStorePallet,
	isScanToCreateEnabled,
	isScanToCreateActive,
	...rest
}) => {
	const notify = useNotify();
	const notifyError = useNotifyError();
	const { resource, basePath, displayedFilters, filterValues, showFilter, data, ids, refetch } =
		useListContext(rest);
	const translate = useTranslate();
	const { permissions } = usePermissions();
	const theme = useTheme();
	const activeButtonStyle = {
		backgroundColor: theme.palette.error.main,
		color: theme.palette.error.contrastText,
	};

	const isAdmin = permissions && permissions.role === UserRole.admin;

	const { isOutgoingPlukk, isCrossDock, isCSOrIncomingPlukk } = useOrderServiceType(order);
	const {
		isSupported: isPalletGenerationSupported,
		isBlockedByStatus: isPalletGenerationBlockedByStatus,
	} = usePalletGenerationGate(order);

	const isCSOrIncomingPlukkScannerMode =
		isCSOrIncomingPlukk &&
		(envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE ||
			isScannerOperationMode(operationMode));
	const recordPrepPlaceId = envSettings.REACT_APP_FF_STORAGE_MODULE
		? order.storage_place_id
		: order.area_id;
	const isMoveToPreparationAllowed = order.status === OrderStatus.PLUKK_STARTED;
	// Admin users can load pallets in LOADING_COMPLETED and ORDER_COMPLETED statuses to fix issues
	const isLoadingIntoTruckAllowed =
		order?.status === OrderStatus.LOADING_STARTED ||
		(isAdmin &&
			(order?.status === OrderStatus.LOADING_COMPLETED ||
				order?.status === OrderStatus.ORDER_COMPLETED));

	const isLoadingCompletedAllowed = React.useMemo(
		() =>
			isCSOrIncomingPlukk &&
			order.status !== OrderStatus.LOADING_COMPLETED &&
			order.status !== OrderStatus.ORDER_COMPLETED,
		[isCSOrIncomingPlukk, order]
	);

	const pallets = ids?.map((id) => data[id]) || [];

	const palletExportFilters = {
		is_inventory_list: false,
		order_id: order.id,
	};
	const handlePrintCMR = async () => {
		await downloadFileWithNotification(
			`order/${order.id}/cmr-export`,
			`cmr_export_order_${order.id}.pdf`,
			notifyError
		);
	};

	const handlePalletsInStorage = () => {
		const ids = Array.from(new Set(pallets.map((p) => p.storage_place_id).filter(Boolean)));
		if (ids.length === 0) {
			notify(translate("tab.pallets.no_pallets_in_storage"), { type: "warning" });
			return;
		}

		const placesParam = `[${ids.join(",")}]`;
		const url = `#/storage-ui?places=${placesParam}&terminal_id=${order.terminal_id}`;
		window.open(url, "_blank");
	};

	const handleAllocateStoragePlaces = () => {
		if (!order?.id) return;

		const unallocatedPalletIds = pallets.filter((p) => allowStorePallet(p)).map((p) => p.id);

		if (unallocatedPalletIds.length === 0) {
			notify(translate("tab.pallets.all_pallets_allocated"), { type: "info" });
			return;
		}

		const confirmed = window.confirm(
			translate("tab.pallets.confirm_allocate_storage_places", {
				count: unallocatedPalletIds.length,
			})
		);

		if (!confirmed) return;
		setAllocationLoading(true);

		httpClient(`order/${order.id}/pallets/allocate-storage-places`, {
			method: "POST",
			body: JSON.stringify({
				pallet_ids: unallocatedPalletIds,
				apply_allocation: true,
			}),
		})
			.then(({ json }) => {
				refetch();
				setAllocationResult(json);
				setAllocationResultOpen(true);
			})
			.catch((err) => {
				notifyError(err);
			})
			.finally(() => {
				setAllocationLoading(false);
			});
	};

	const handleLoadingCompleted = () => {
		setStatusChangeLoading(true);
		httpClient(`orders/${order.id}`, {
			method: "PATCH",
			body: JSON.stringify({ status: OrderStatus.LOADING_COMPLETED }),
		})
			.then(() => {
				notify(translate("tab.pallets.loading_completed_success"), { type: "success" });
				window.location.reload();
			})
			.catch((err) => {
				notifyError(err);
			})
			.finally(() => {
				setStatusChangeLoading(false);
			});
	};

	const onLoadingSubmit = () => {
		setOperationMode(defaultOperationMode);
		httpClient(`/pallets`, {
			method: "PATCH",
			body: JSON.stringify({
				ids: selectedPalletsForLoading,
				data: { loaded_into_truck: true, order_id: order.id },
			}),
		})
			.then(({ json }) => {
				if (json && Array.isArray(json)) {
					const responsePalletIds = json.map((pallet) => pallet.id);
					const requestPalletIds = selectedPalletsForLoading;
					const notProcessedIds = requestPalletIds.filter((id) => !responsePalletIds.includes(id));

					if (notProcessedIds.length > 0) {
						notify(
							translate("tab.pallets.warning_not_all_processed", {
								ids: notProcessedIds.join(", "),
								count: notProcessedIds.length,
							}),
							{ type: "warning" }
						);
					}
				}

				refetch();
				notify(translate("tab.pallets.success"), "success");
				setIsLoadingMode(false);
				setSelectedPalletsForLoading([]);
			})
			.catch((err) => {
				notifyError(err);
			});
	};

	const onMoveToPreparation = () => {
		const newMode = !isMoveToPreparationMode;
		setIsMoveToPreparationMode(newMode);
		if (newMode) {
			setIsLoadingMode(false);
			setOperationMode(defaultOperationMode);
		} else {
			setSelectedPalletsForLoading([]);
			setOperationMode(defaultOperationMode);
		}
	};

	const onLoad = () => {
		const newMode = !isLoadingMode;
		setIsLoadingMode(newMode);
		if (newMode) {
			setOperationMode(defaultOperationMode);
			setIsMoveToPreparationMode(false);
		} else {
			setSelectedPalletsForLoading([]);
			setOperationMode(defaultOperationMode);
		}
	};

	const onMoveToPreparationSubmit = async () => {
		if (!recordPrepPlaceId) {
			notify(translate("tab.pallets.error_preparation_place_required"), { type: "warning" });
			return;
		}
		setOperationMode(defaultOperationMode);
		const selectedPallets = pallets.filter((pallet) =>
			selectedPalletsForLoading.includes(pallet.id)
		);
		const opPalletIds = selectedPallets
			.filter((pallet) => pallet.is_target)
			.map((pallet) => pallet.id);
		const normalPalletIds = selectedPallets
			.filter((pallet) => !pallet.is_target)
			.map((pallet) => pallet.id);

		const requests = [];
		if (normalPalletIds.length) {
			requests.push(
				httpClient("pallets", {
					method: "PATCH",
					body: JSON.stringify({
						ids: normalPalletIds,
						data: { into_preparation_place: true, order_id: order.id },
					}),
				})
			);
		}
		if (opPalletIds.length) {
			requests.push(
				Promise.all(
					opPalletIds.map((palletId) =>
						httpClient(`pallets/${palletId}`, {
							method: "PATCH",
							body: JSON.stringify({ storage_place_id: order.storage_place_id }),
						})
					)
				)
			);
		}

		try {
			await Promise.all(requests);
			refetch();
			notify(translate("tab.pallets.success"), "success");
			setIsMoveToPreparationMode(false);
			setSelectedPalletsForLoading([]);
		} catch (err) {
			notifyError(err);
		}
	};

	return (
		<TopToolbar {...sanitizeListRestProps(rest)}>
			<Box
				display="flex"
				justifyContent="flex-end"
				alignItems="center"
				flex={isCSOrIncomingPlukkScannerMode ? 1 : "auto"}
				marginLeft={isCSOrIncomingPlukkScannerMode ? 0 : "auto"}
				style={{ gap: "8px" }}
			>
				{isPalletGenerationSupported && !isCSOrIncomingPlukkScannerMode && (
					<>
						<GenerateBySourceDocPalletsButton
							order={order}
							disabled={isPalletGenerationBlockedByStatus}
						/>
						<PalletsFromPdfButton
							order={order}
							disabled={isPalletGenerationBlockedByStatus}
						/>
					</>
				)}
				{/* Loading Completed Button */}
				{isCSOrIncomingPlukk && (
					<Button
						variant="contained"
						color="primary"
						onClick={handleLoadingCompleted}
						disabled={!isLoadingCompletedAllowed || statusChangeLoading}
					>
						{statusChangeLoading && (
							<CircularProgress size={18} thickness={2} style={{ marginRight: 8 }} />
						)}
						{translate("tab.pallets.loading_completed_button")}
					</Button>
				)}

				{isScanToCreateEnabled && envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE && (
					<Button
						variant="contained"
						color={isScanToCreateActive ? undefined : "primary"}
						onClick={() =>
							setOperationMode(
								isScanToCreateActive ? defaultOperationMode : scanToCreateModeOption.value
							)
						}
						style={isScanToCreateActive ? activeButtonStyle : undefined}
					>
						{translate("tab.pallets.scan_to_create")}
					</Button>
				)}

				{/* Outgoing Plukk / Cross-dock Actions */}
				{(isOutgoingPlukk || isCrossDock) &&
					pallets.length > 0 &&
					!isCSOrIncomingPlukkScannerMode && (
						<>
							{recordPrepPlaceId && (
								<>
									<Button
										variant="contained"
										color={isMoveToPreparationMode ? undefined : "primary"}
										onClick={onMoveToPreparation}
										style={isMoveToPreparationMode ? activeButtonStyle : undefined}
										disabled={isLoadingMode || !isMoveToPreparationAllowed}
									>
										{isMoveToPreparationMode
											? translate("osfc.buttons.cancel")
											: translate("tab.pallets.move_to_preparation_place")}
									</Button>
									{isMoveToPreparationMode && (
										<Button
											variant="contained"
											color="primary"
											onClick={onMoveToPreparationSubmit}
											disabled={
												selectedPalletsForLoading.length === 0 || !isMoveToPreparationAllowed
											}
										>
											{translate("osfc.buttons.submit")}
										</Button>
									)}
								</>
							)}
							<Button
								variant="contained"
								color={isLoadingMode ? undefined : "primary"}
								onClick={onLoad}
								style={isLoadingMode ? activeButtonStyle : undefined}
								disabled={isMoveToPreparationMode || !isLoadingIntoTruckAllowed}
							>
								{isLoadingMode
									? translate("osfc.buttons.cancel")
									: translate("tab.pallets.loading_into_truck")}
							</Button>
							{isLoadingMode && (
								<Button
									variant="contained"
									color="primary"
									onClick={onLoadingSubmit}
									disabled={selectedPalletsForLoading.length === 0 || !isLoadingIntoTruckAllowed}
								>
									{translate("osfc.buttons.submit")}
								</Button>
							)}
						</>
					)}

				{!isLoadingMode && !isMoveToPreparationMode && (
					<>
						<DownloadPalletsExportDropdown
							filters={palletExportFilters}
							filenamePrefix={`pallets_export_order_${order.id}`}
						/>

						{isOutgoingPlukk && (
							<Button variant="contained" color="primary" onClick={handlePrintCMR}>
								{translate("tab.picking_list.download_cmr")}
							</Button>
						)}
					</>
				)}

				{/* Other Actions */}
				{isCSOrIncomingPlukk && pallets.length > 0 && (
					<DownloadPalletsLabelsDropdown order={order} />
				)}

				{(isCSOrIncomingPlukk || isCrossDock) &&
					pallets.length > 0 &&
					(envSettings.REACT_APP_FF_COMBINED_SCANNER_MANUAL_MODE ||
						!isScannerOperationMode(operationMode)) &&
					envSettings.REACT_APP_FF_STORAGE_MODULE && (
						<Button
							variant="outlined"
							size="small"
							onClick={handleAllocateStoragePlaces}
							disabled={allocationLoading}
						>
							{allocationLoading && (
								<CircularProgress size={18} thickness={2} style={{ marginRight: 8 }} />
							)}
							{translate("tab.pallets.allocate_storage_places")}
						</Button>
					)}

				{pallets.length > 0 && envSettings.REACT_APP_FF_STORAGE_MODULE && (
					<Button variant="outlined" size="small" onClick={handlePalletsInStorage}>
						{translate("tab.pallets.pallets_in_storage")}
					</Button>
				)}
			</Box>

			{filters &&
				React.cloneElement(filters, {
					resource,
					showFilter,
					displayedFilters,
					filterValues,
					context: "button",
				})}
		</TopToolbar>
	);
};
